/**
 * Coach Engine — Day Scope
 *
 * Orchestrates day-scope analyzers. Takes the selected trading day + full
 * user context, builds a DayContext (one pass, shared metrics), runs every
 * analyzer, and returns ranked observations.
 *
 * Adding a new day-scope analyzer:
 *   1. Create the module under ../analyzers/day/
 *   2. Import + push into ANALYZERS below (respecting tier)
 *   3. Done — ranking, dedup, scope dispatch all wire up automatically.
 *
 * Tier gating:
 *   Each analyzer exports a `tier` constant ('free' | 'pro'). The scope
 *   takes an optional `tier` in ctx and filters analyzers to the tier
 *   level the user has paid for. Defaults to 'free' so nothing breaks.
 */

import { buildDayContext, computeGrade } from '../dayUtils';
import { topObservations } from '../priorities';
import { synthesizeAndPrepend } from '../synthesis/synthesize';
import { enrichWithTemporalContext } from '../synthesis/temporalContext';
import { getRuntimeContext } from '../profile/runtimeContext';
import { SEVERITY, CATEGORY, makeObservation } from '../types';

// Day-scope analyzers
import * as dayGrade            from '../analyzers/day/dayGrade';
import * as dayPerformance      from '../analyzers/day/dayPerformance';
import * as dayMistakes         from '../analyzers/day/dayMistakes';
import * as daySetupSpotlight   from '../analyzers/day/daySetupSpotlight';
import * as dayAtmSpotlight     from '../analyzers/day/dayAtmSpotlight';
import * as dayTimeOfDay        from '../analyzers/day/dayTimeOfDay';
import * as dayFirstTradeEffect from '../analyzers/day/dayFirstTradeEffect';
import * as dayDurationProfile  from '../analyzers/day/dayDurationProfile';
import * as dayOvertrading      from '../analyzers/day/dayOvertrading';
import * as dayDrawdown         from '../analyzers/day/dayDrawdown';
import * as dayBaselineCompare  from '../analyzers/day/dayBaselineCompare';
import * as dayNewsEventImpact  from '../analyzers/day/dayNewsEventImpact';
import * as dayEventCitations   from '../analyzers/day/dayEventCitations';
import * as dayBarContext       from '../analyzers/day/dayBarContext';
import * as dayFlagRollup       from '../analyzers/day/dayFlagRollup';
import * as dayPlanAdherence    from '../analyzers/day/dayPlanAdherence';
import * as dayExperiments      from '../analyzers/day/dayExperiments';
import * as dayOneGoodTrade     from '../analyzers/day/dayOneGoodTrade';
import * as dayProcessSubgrades from '../analyzers/day/dayProcessSubgrades';
import * as emaProximityDay     from '../analyzers/composite/emaProximityDay';
import * as atmParamDirection   from '../analyzers/composite/atmParamDirection';

const ANALYZERS = [
  dayGrade,
  dayPerformance,
  dayMistakes,
  daySetupSpotlight,
  dayAtmSpotlight,
  dayTimeOfDay,
  dayFirstTradeEffect,
  dayDurationProfile,
  dayOvertrading,
  dayDrawdown,
  dayBaselineCompare,
  dayNewsEventImpact,
  dayEventCitations,
  dayBarContext,
  dayFlagRollup,
  // Day-level plan adherence — Phase 15. Reads journalEntry.plan + matches
  // mentioned setup names against actually-traded setups.
  dayPlanAdherence,
  // Day-level experiment fan-out — Phase 16. For each experiment with a
  // trade today, runs experimentComparison across the experiment's full
  // cross-day history so multi-day A/B tests are evaluated as one unit.
  dayExperiments,
  // Wave B.4 — SMB Capital "One Good Trade" counter. Surfaces process
  // completion as a separate dimension from win count, so green days
  // don't validate sloppy execution and red days don't punish discipline.
  dayOneGoodTrade,
  // Wave B.1 — SMB Capital "Daily Report Card" sub-grades. Decomposes
  // the overall day grade into Sizing / Patience / Exit / Plan so the
  // user can see WHY their grade is what it is. Each sub-grade is
  // independent — the analyzer emits structured evidence the UI renders
  // as a 4-up grid below the overall letter.
  dayProcessSubgrades,
  // Composite — day-level chase-rate alarm. Surfaces when too many of
  // today's entries were extended (>1.0 stops from nearest EMA), or
  // praises the day when the discipline held.
  emaProximityDay,
  // Phase 17 — cross-ATM parameter direction. "Meta-edge": across all
  // templates, which numeric param + direction (e.g. widening Trail SL)
  // has consistently improved WR. Fires at day scope so it's visible
  // in the daily review even when no single template is being analyzed.
  atmParamDirection,
];

// Tier ordering: each user-level grants access to that level and below
const TIER_LEVEL = { free: 0, pro: 1 };

/**
 * Filter analyzers by the user's tier. Defaults to 'free'.
 */
const analyzersForTier = (userTier = 'free') => {
  const level = TIER_LEVEL[userTier] ?? 0;
  return ANALYZERS.filter(a => {
    const t = a.tier || 'free';
    return (TIER_LEVEL[t] ?? 0) <= level;
  });
};

/**
 * Build a fallback observation when no trades exist on the day.
 */
const emptyDayObservation = (date) => makeObservation({
  id: `fallback:day:${date}:empty`,
  analyzer: 'fallback',
  scope: 'day',
  scopeId: date,
  severity: SEVERITY.INFO,
  category: CATEGORY.PROCESS,
  title: 'No trades recorded for this day',
  detail: 'Once trades are logged, the Coach will grade the day and surface patterns.',
  confidence: 1,
  priority: 10
});

/**
 * Analyze a trading day. Returns ranked observations (top N by default).
 *
 * @param {Object} args
 * @param {string}   args.date                YYYY-MM-DD
 * @param {Array}    args.trades              Trades filtered to this day
 * @param {Array}    args.allTrades           Full history (for baseline)
 * @param {Object}   args.journalEntry        Today's journal entry ({eventTags, plan, notes, newsEvents})
 * @param {Object}   args.allJournalEntries   All journal entries keyed by date (for event-day comparison)
 * @param {Array}    args.accounts            For cluster labels
 * @param {Function} args.getTradeNetPnL
 * @param {Function} args.getAccountById
 * @param {string}   [args.tier='free']       'free' | 'pro'
 * @param {number}   [args.top=6]             How many observations to return
 * @returns {{ observations: Observation[], grade: Object, day: DayContext|null }}
 */
export const analyzeDay = ({
  date,
  trades = [],
  allTrades = [],
  journalEntry = {},
  allJournalEntries = {},
  accounts = [],
  getTradeNetPnL,
  getAccountById,
  tier = 'free',
  top = 6,
  // Phase 15 — passthrough for dayPlanAdherence's setup-name vocabulary.
  // Caller (TradingJournal/JournalView) supplies its known setups list.
  knownSetups = [],
  // Phase 17 — NinjaTrader ATM version history keyed by ATM name. Threaded
  // through so the cross-ATM analyzers (atmParamDirection) can read the
  // full version arc. Optional — undefined silently disables those analyzers.
  ntTemplateVersions = {},
} = {}) => {
  if (!date) {
    return { observations: [], grade: computeGrade(null), day: null };
  }

  const day = buildDayContext({
    date, trades, allTrades, journalEntry, accounts,
    getTradeNetPnL, getAccountById
  });

  if (!day) {
    return {
      observations: [emptyDayObservation(date)],
      grade: computeGrade(null),
      day: null
    };
  }

  const ctx = {
    day,
    allTrades,
    allJournalEntries,
    accounts,
    getTradeNetPnL,
    getAccountById,
    tier,
    // Phase 15 — pass through journalEntry + knownSetups for the
    // dayPlanAdherence analyzer (and any future analyzers that need
    // setup/symbol vocab for parsing free-text fields).
    journalEntry,
    knownSetups,
    // Phase 17 — passed to atmParamDirection so it can scan version
    // history across all ATM templates for cross-cutting param trends.
    ntTemplateVersions,
  };

  const observations = [];
  for (const analyzer of analyzersForTier(tier)) {
    try {
      const result = analyzer.analyze(ctx);
      if (Array.isArray(result)) observations.push(...result.filter(Boolean));
      else if (result) observations.push(result);
    } catch (err) {
      console.warn(`[Coach/day] analyzer "${analyzer.key || 'unknown'}" threw:`, err);
    }
  }

  // Profile-aware enrichment first, so synthesis sees the temporal context
  // (and the temporal-narrative clause it appends).
  const enriched = enrichWithTemporalContext(observations, getRuntimeContext());
  // Synthesis pass — fuses the day's analyzer observations into a single
  // "State of today" narrative pinned at the top of the feed. The
  // underlying observations remain available for drill-down below it.
  const withSynthesis = synthesizeAndPrepend(enriched, {
    scope: 'day',
    scopeId: ctx.date || '',
    tradeCount: day?.trades?.length || 0,
  });

  return {
    observations: topObservations(withSynthesis, top),
    grade: computeGrade(day),
    day
  };
};

/**
 * Legacy-shape adapter. Converts the coach-engine day output back into the
 * object shape the existing Daily Review modal expects (see TradingJournal.js
 * around line 4055). This is a temporary compatibility layer so we can rewire
 * the modal to the Coach pipeline without touching 600+ lines of JSX.
 *
 * Shape reference (from the legacy generateDailyReview):
 *   { summary, grade, stats, context, analysis: { setups, atm, mistakes,
 *     time, accounts, comparison }, feedback }
 *
 * @param {Object} args
 * @param {Object} args.day                  DayContext from buildDayContext
 * @param {Observation[]} args.observations  Ranked day-scope observations
 * @param {Object} args.grade                { letter, color, score }
 * @param {Object} args.allJournalEntries    For similar-event-day comparison
 * @param {Array}  args.allTrades            For similar-event-day P&L lookup
 * @param {Function} args.getTradeNetPnL
 * @param {Function} args.getAccountById
 * @returns {Object} legacy review shape
 */
export const toLegacyReviewShape = ({
  day,
  observations = [],
  grade,
  allJournalEntries = {},
  allTrades = [],
  getTradeNetPnL,
  getAccountById
}) => {
  if (!day || !day.core) {
    return {
      summary: 'No trades recorded for this day.',
      stats: null,
      context: {
        eventTags: [],
        plan: '',
        notes: '',
        newsEvents: []
      },
      feedback: []
    };
  }

  const { core, setups, atm, mistakes, time, accounts, journalEntry, baseline, trades } = day;

  // ── Summary line ────────────────────────────────────────────────────────
  const summary = `${core.tradesCount} trades: ${core.winnersCount}W / ${core.losersCount}L${core.breakevenCount > 0 ? ` / ${core.breakevenCount}BE` : ''}`;

  // ── Stats ───────────────────────────────────────────────────────────────
  const stats = {
    totalPnL: core.totalPnL,
    winRate: core.winRate * 100,
    avgR: core.avgR,
    totalR: core.totalR,
    profitFactor: core.profitFactor,
    avgWin: core.avgWin,
    avgLoss: core.avgLoss,
    bestTrade: core.bestTrade || { pnl: 0, trade: null },
    worstTrade: core.worstTrade || { pnl: 0, trade: null },
    maxDrawdown: core.maxDrawdown,
    tradesCount: core.tradesCount,
    winnersCount: core.winnersCount,
    losersCount: core.losersCount
  };

  // ── Context (journal) ───────────────────────────────────────────────────
  const context = {
    eventTags: Array.isArray(journalEntry?.eventTags) ? journalEntry.eventTags : [],
    plan: journalEntry?.plan || '',
    notes: journalEntry?.notes || '',
    newsEvents: Array.isArray(journalEntry?.newsEvents) ? journalEntry.newsEvents : []
  };

  // ── Analysis.setups — legacy used string winRate with .toFixed(1) ───────
  const setupsLegacy = (setups || []).map(s => ({
    name: s.name, trades: s.trades, pnl: s.pnl,
    winRate: s.winRate.toFixed(1)
  }));

  // ── Analysis.atm — legacy used string winRate + string avgR ─────────────
  const atmLegacy = {
    strategies: (atm?.strategies || []).map(a => ({
      name: a.name, trades: a.trades, pnl: a.pnl,
      winRate: a.winRate.toFixed(1),
      avgR: a.avgR.toFixed(2)
    })),
    tradesWithAtm: atm?.tradesWithAtm || 0,
    totalAtmTrades: atm?.tradesWithAtm || 0
  };

  // ── Analysis.mistakes — match legacy keys ──────────────────────────────
  const mistakesLegacy = {
    counts: mistakes?.counts || {},
    pnl: mistakes?.mistakePnL || 0,
    cleanPnL: mistakes?.cleanPnL || 0,
    tradesWithMistakes: mistakes?.tradesWithMistakes || 0
  };

  // ── Analysis.time — rebuild the legacy duration sub-shape ──────────────
  let timeLegacy = null;
  if (time) {
    const durLegacy = time.duration ? {
      avgDuration: time.duration.avgDuration,
      avgWinDuration: time.duration.avgWinDuration,
      avgLossDuration: time.duration.avgLossDuration,
      // Legacy exposed explicit shortest/longest trades — derive from timed+exits
      shortestTrade: (() => {
        const t = trades.filter(x => x.entryTime && x.exitTime);
        if (t.length === 0) return { duration: 0, pnl: 0 };
        // best-effort: pick the one nearest 0-5min bucket w/ lowest duration
        const withDur = t
          .map(x => {
            const entry = x.entryTime, exit = x.exitTime;
            const parse = (s) => {
              if (!s) return null;
              const str = String(s).includes('T') ? String(s).split('T')[1] : String(s);
              const [h, m] = str.split(':').map(n => parseInt(n, 10));
              return (isNaN(h) || isNaN(m)) ? null : h * 60 + m;
            };
            const a = parse(entry), b = parse(exit);
            return { ...x, duration: (a !== null && b !== null) ? (b - a) : null };
          })
          .filter(x => x.duration !== null && x.duration > 0);
        if (withDur.length === 0) return { duration: 0, pnl: 0 };
        const short = withDur.reduce((min, x) => x.duration < min.duration ? x : min);
        return { duration: short.duration, pnl: short.netPnl };
      })(),
      longestTrade: { duration: 0, pnl: 0 },  // not critical for rendering; legacy modal doesn't reference it often
      quickTrades: time.duration.quick || { count: 0, pnl: 0, winRate: 0 },
      longerTrades: time.duration.longer || { count: 0, pnl: 0, winRate: 0 }
    } : null;

    const bucketLegacy = (b) => ({
      trades: b.trades,
      pnl: b.pnl,
      winRate: b.winRate.toFixed(1)
    });

    const hourlyStatsLegacy = {};
    (time.hourly || []).forEach(h => {
      hourlyStatsLegacy[h.hour] = { trades: h.trades, pnl: h.pnl, wins: h.wins };
    });

    timeLegacy = {
      morning:   bucketLegacy(time.buckets.morning),
      midday:    bucketLegacy(time.buckets.midday),
      afternoon: bucketLegacy(time.buckets.afternoon),
      hourlyStats: hourlyStatsLegacy,
      bestHour:  time.bestHour && time.bestHour.hour !== null ? time.bestHour : null,
      worstHour: time.worstHour && time.worstHour.hour !== null ? time.worstHour : null,
      duration: durLegacy,
      firstTrade: time.firstTrade,
      restOfDay: time.restOfDay ? {
        trades: time.restOfDay.trades,
        pnl: time.restOfDay.pnl,
        winRate: time.restOfDay.winRate.toFixed(1)
      } : null,
      tradeClusters: (time.clusters && time.clusters.length > 0) ? time.clusters : null
    };
  }

  // ── Analysis.comparison (baseline + event days) ─────────────────────────
  let comparisonLegacy = null;
  if (baseline) {
    comparisonLegacy = {
      avgDailyPnL: baseline.avgDailyPnL,
      avgDailyTrades: baseline.avgDailyTrades,
      overallWinRate: baseline.overallWinRate,
      todayVsAvg: core.totalPnL - baseline.avgDailyPnL,
      eventComparison: null
    };

    // Similar-event-day comparison (mirrors legacy + dayNewsEventImpact analyzer)
    const todayTags = context.eventTags;
    if (todayTags.length > 0 && Object.keys(allJournalEntries).length > 1 && Array.isArray(allTrades)) {
      const similarDates = Object.entries(allJournalEntries)
        .filter(([d, entry]) =>
          d !== day.date &&
          Array.isArray(entry?.eventTags) &&
          entry.eventTags.some(tag => todayTags.includes(tag)))
        .map(([d]) => d);
      if (similarDates.length >= 1) {
        const netFor = (t) => {
          const acc = getAccountById ? getAccountById(t.accountId) : null;
          const r = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
          return isFinite(r?.netPnl) ? r.netPnl : 0;
        };
        const eventTrades = allTrades.filter(t => similarDates.includes(t.date));
        const eventPnL = eventTrades.reduce((s, t) => s + netFor(t), 0);
        const avgEventPnL = similarDates.length > 0 ? eventPnL / similarDates.length : 0;
        comparisonLegacy.eventComparison = {
          similarDays: similarDates.length,
          avgPnL: avgEventPnL,
          todayVsAvg: core.totalPnL - avgEventPnL
        };
      }
    }
  }

  // ── Feedback — map observations to the legacy feedback-card shape ──────
  const severityToType = (sev) => {
    if (sev === 'success') return 'success';
    if (sev === 'warning' || sev === 'critical') return 'warning';
    return 'info';
  };
  const feedback = observations.map(obs => ({
    type: severityToType(obs.severity),
    icon: 'coach',   // modal picks icon from type; this is a decorative label
    title: obs.title,
    message: [obs.detail, obs.action ? `→ ${obs.action}` : null].filter(Boolean).join(' ')
  }));

  return {
    summary,
    grade: grade ? { letter: grade.letter, color: grade.color, score: grade.score } : null,
    stats,
    context,
    analysis: {
      setups: setupsLegacy,
      atm: atmLegacy,
      mistakes: mistakesLegacy,
      time: timeLegacy,
      accounts,
      comparison: comparisonLegacy
    },
    feedback
  };
};

export default { analyzeDay, toLegacyReviewShape };
